<template>
  <li class="podcasts__item">
    <router-link :to="view.startLinkParams(doc)" class="podcasts__link">
      <div class="podcasts__img-box">
        <span
          v-if="view.hasFollowBadge() && doc.is_followed"
          class="podcasts__badge"
          >Following</span
        >

        <component
          :is="lazyComponent"
          :src="doc.cover_image"
          crossorigin="anonymous"
          class="podcasts__img"
          alt=""
        />
      </div>
      <div class="podcasts__text-box">
        <h3 class="podcasts__title">{{ doc.title }}</h3>
        <h4 class="podcasts__subtitle">{{ view.podcastSubtitle(doc) }}</h4>
      </div>
    </router-link>
  </li>
</template>

<script setup lang="ts">
import { IS_PRERENDERING } from '@/init/settings'
import { DocGroupListItem, ListItem } from '@/models/interfaces'
import LazyImage from './LazyImage.vue'
import PublicImage from './PublicImage.vue'
import { PodcastsView } from '@/models/docs.list.podcasts'
import { computed } from 'vue'

interface PodcastViewProps extends Partial<PodcastsView> {
  startLinkParams(doc: ListItem): Record<string, unknown>
  hasFollowBadge(): boolean
  podcastSubtitle(podcast: DocGroupListItem): string
}

defineProps<{
  doc: DocGroupListItem
  view: PodcastViewProps
}>()

/* Lazy load is disabled for static pages.

  Note: When accessing the static page locally, we will not see any cover_images 
  unless we've build them using the build.sh script.
  
  */

const lazyComponent = computed(() => {
  return !IS_PRERENDERING ? LazyImage : PublicImage
})
</script>

<style lang="scss">
.podcasts {
  &__item {
    list-style-type: none;
  }
  &__link {
    display: flex;
    flex-direction: column;
    gap: 20px;
    height: 100%;
    padding: 20px;
    background-color: $c-bg;
    border-radius: 8px;
    border: 1px solid $c-border-7;

    @media only screen and (max-width: $w-tablet-s) {
      flex-direction: row;
      padding: 16px;
    }

    @include hover {
      border-color: $c-border-2;
      box-shadow: 0px 10px 10px rgb(20 28 49 / 3%);

      .podcasts__img {
        transform: scale(1.07);
      }
    }
  }

  &__img-box {
    position: relative;
    overflow: hidden;
    flex-shrink: 0;
    border-radius: 4px;
  }

  &__badge {
    position: absolute;
    z-index: 1;
    left: 0;
    top: 0;
    padding: 4px 8px 2px 8px;
    border-radius: 0 0 4px 0;
    font-size: 12px;
    line-height: 18px;
    color: $c-black-3;
    background: rgba($color: $c-border-7, $alpha: 0.8);
  }

  &__img {
    width: 100%;
    aspect-ratio: 1/1;
    object-fit: contain;
    transition: transform 0.2s ease-in-out;

    @media only screen and (max-width: $w-tablet-s) {
      width: 104px;
    }
  }

  &__text-box {
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    justify-content: space-between;

    @media only screen and (max-width: $w-tablet-s) {
      flex-grow: 0;
      justify-content: center;
    }
  }

  &__title {
    font-size: 16px;
    line-height: 18px;
    color: $c-black-2;
    margin-bottom: 0;

    @media only screen and (max-width: $w-tablet-s) {
      font-size: 16px;
      line-height: 20px;
      overflow: hidden;
      display: -webkit-box;
      -webkit-line-clamp: 3;
      -webkit-box-orient: vertical;
      text-overflow: ellipsis;
    }
  }

  &__subtitle {
    margin-top: 8px;
    font-size: 12px;
    line-height: 16px;
    font-weight: 400;
    color: $c-black-3;
    margin-bottom: 0;

    @media only screen and (max-width: $w-tablet-s) {
      font-size: 12px;
      line-height: 14px;
    }
  }
}

.night-mode .podcasts {
  &__link {
    background-color: $c-dark-2;
    border-color: $c-dark-3;

    @include hover {
      border-color: $c-dark-8;
      box-shadow: none;
    }
  }

  &__badge {
    color: $c-dark-4;
    background: rgba($color: $c-dark-8, $alpha: 0.8);
  }

  &__title {
    color: $c-dark-4;
  }

  &__subtitle {
    color: $c-black-6;
  }
}
</style>
